import React, { FC, useState, useEffect } from 'react';
import axiosInstance from 'config/axiosInstance';
import { useTranslation } from 'react-i18next';

export interface Task {
  id: number;
  name: string;
  icon: string;
  isDisabled: boolean;
}

interface TaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
  tasks?: Task[];
  onAddTasks: (selectedTaskIds: number[]) => void;
}

const AddTaskDialog: FC<TaskDialogProps> = ({
  isOpen,
  onClose,
  tasks = [],
  onAddTasks,
}) => {
  const { t } = useTranslation('client');
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<Task[]>(tasks);

  const fetchTasksData = async () => {
    try {
      const tasksResponse = await axiosInstance.get(`/v1/tasks/?page_size=15`);
      setAllTasks(tasksResponse.data.results);
    } catch (error) {
      console.error('Error fetching tasks data:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTasksData();
      setSelectedTasks(tasks);
    }
  }, [isOpen, tasks]);

  const handleCheckboxChange = (task: Task, isChecked: boolean) => {
    setSelectedTasks((prevSelectedTasks) => {
      if (isChecked) {
        return [...prevSelectedTasks, task];
      } else {
        return prevSelectedTasks.filter(
          (selectedTask) => selectedTask.id !== task.id
        );
      }
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const selectedTaskIds = selectedTasks.map((task) => task.id);
    onAddTasks(selectedTaskIds);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 w-[600px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
          tabIndex={-1}
        >
          <div className="bg-white">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <div className="flex justify-between items-center mt-4 mx-6">
                  <h3
                    className="text-subtitle-1 font-medium text-neutral/500"
                    id="modal-headline"
                  >
                    {t('action.add_tasks')}
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <hr className="border-neutral/50 mt-4 w-full" />
                <form>
                  <div className="grid grid-cols-3 gap-2 p-6">
                    {allTasks.map((task) => (
                      <TaskItem
                        key={task.id}
                        id={task.id}
                        name={task.name}
                        icon={task.icon}
                        isDisabled={
                          selectedTasks.find(
                            (selectedTask) => selectedTask.id === task.id
                          )?.isDisabled || false
                        }
                        isChecked={selectedTasks.some(
                          (selectedTask) => selectedTask.id === task.id
                        )}
                        onCheckboxChange={(isChecked) =>
                          handleCheckboxChange(task, isChecked)
                        }
                      />
                    ))}
                  </div>
                  <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center items-center rounded-md border bg-primary/50 text-subtitle-2 text-primary/300"
                      onClick={onClose}
                    >
                      {t('action.cancel')}
                    </button>
                    <button
                      type="button"
                      className="w-full inline-flex justify-center items-center rounded-md bg-primary/300 text-subtitle-2 text-white h-12"
                      onClick={handleSubmit}
                    >
                      {t('action.add_tasks')} ({selectedTasks.length})
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface TaskItemProps {
  id: number;
  name: string;
  icon: React.ReactNode;
  isDisabled: boolean;
  onCheckboxChange: (isChecked: boolean) => void;
  isChecked: boolean;
}

const TaskItem: FC<TaskItemProps> = ({
  id,
  name,
  icon,
  isDisabled,
  onCheckboxChange,
  isChecked,
}) => {
  const [checked, setChecked] = useState(isChecked);
  const [iconSrc, setIconSrc] = useState<string | null>(null);

  useEffect(() => {
    const loadIcon = async () => {
      try {
        const importedIcon = await import(
          `../../../assets/icons/tasks/${icon}.svg`
        );
        setIconSrc(importedIcon.default);
      } catch (err) {
        console.error(`Icon not found: ${icon}`, err);
        setIconSrc(null);
      }
    };

    loadIcon();
  }, [icon]);

  useEffect(() => {
    setChecked(isChecked);
  }, [isChecked]);

  const handleCheckboxChange = () => {
    if (!isDisabled) {
      const newChecked = !checked;
      setChecked(newChecked);
      onCheckboxChange(newChecked);
    }
  };

  return (
    <label
      className={`flex flex-col items-start p-2 border rounded-md shadow-sm w-[175px] ${
        checked ? 'bg-secondary/100 border-neutral/75' : 'bg-white neutral/50'
      } ${isDisabled ? 'opacity-30 cursor-not-allowed' : ''} `}
    >
      <span className="text-gray-900 font-medium text-body-2">{name}</span>
      <div className="flex items-end justify-between w-full">
        <input
          className="peer relative appearance-none shrink-0 w-5 h-5 border-2 border-neutral/50 rounded-sm mt-1 bg-white checked:bg-success checked:border-0 disabled:border-steel-400 disabled:bg-steel-400"
          type="checkbox"
          checked={checked}
          onChange={handleCheckboxChange}
          disabled={isDisabled}
        />
        <svg
          className="absolute w-5 h-5 pointer-events-none hidden peer-checked:block stroke-white mt-1 outline-none"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="8 13 11 16 17 8"></polyline>
        </svg>
        {iconSrc && (
          <img src={iconSrc} alt={`${name} icon`} className="w-8 h-8" />
        )}
      </div>
    </label>
  );
};

export default AddTaskDialog;
